import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { transferCredit } from '@/api/hooks/credit/useTransferCredit'
import * as api from '@/data/api'
import type { AccountRecord } from '@/data/types'
import { availableToGive, settlementPlan } from '@/lib/book'

/**
 * The sequential bulk-settle executor.
 *
 * It deliberately does NOT loop `useTransferCredit`. That hook invalidates eight
 * query keys on every success, so a 20-row run would fire 160 refetches and make
 * the table flicker under the modal for the whole run. This calls
 * `api.transferCredit` directly, tracks per-row state, and invalidates exactly
 * ONCE when the run ends — including when it ends badly, because the rows that
 * DID move have to be reflected either way.
 *
 * It also does not call `api.bulkSettle`, which exists for the same operation as
 * a single call. A single call cannot be watched or stopped mid-flight, and this
 * run is long enough that both matter: the operator has to be able to see which
 * row is moving and halt after it.
 *
 * No optimistic rendering anywhere. Every figure in the report is quoted from a
 * mutation result; the table underneath refreshes off the one invalidation.
 */

export type RowOutcome =
  | { state: 'pending' }
  | { state: 'moving' }
  | { state: 'settled'; moved: number; ref: string; settlementAfter: number }
  | {
      state: 'partial'
      moved: number
      remaining: number
      ref: string
      reason: string
      settlementAfter: number
    }
  | { state: 'skipped'; reason: string }
  | { state: 'failed'; reason: string }
  | { state: 'not-attempted' }

export interface BulkRunRow {
  /** The stored row. A settlement run is money; being stopped is not part of it. */
  account: AccountRecord
  direction: 'give' | 'take'
  /** What zeroing this row requires — |settlement| at plan time. */
  intended: number
  /** What the plan expects to move after caps. */
  planned: number
  /** Cap explanation shown in the plan, e.g. 'capped by their ₹12,400 balance'. */
  capReason: string | null
  /** Non-blocking notes, e.g. leaves them below their open exposure. */
  warnings: string[]
  outcome: RowOutcome
}

export interface BulkRunState {
  runKey: string
  rows: BulkRunRow[]
  status: 'idle' | 'running' | 'stopping' | 'done'
  /** Index of the row currently in flight, for the progress line. -1 when idle. */
  cursor: number
  movedTotal: number
}

/** Outcomes that a retry is allowed to touch. A settled row is never re-submitted. */
const RETRYABLE = new Set<RowOutcome['state']>(['partial', 'failed', 'not-attempted'])

/** After two in a row, stop. A systemic failure should produce one clear halt,
 *  not forty identical error rows the operator has to read past. */
const CONSECUTIVE_FAILURE_LIMIT = 2

export function useBulkSettle(): {
  state: BulkRunState | null
  /** Mints a runKey and seeds every row as `pending`. Does not move money. */
  prepare: (rows: Omit<BulkRunRow, 'outcome'>[]) => void
  /**
   * Replace the FIGURES on rows a retry is about to touch, keeping the runKey
   * and every recorded outcome.
   *
   * `prepare` cannot do this job: it mints a new runKey, which is precisely what
   * a retry must not do, and it resets every outcome to pending, which would
   * re-submit rows that already moved money. Without `reseed`, a retry executed
   * `settlementPlan` against the account SNAPSHOT taken at `prepare` time, while
   * the dialog showed the operator a plan re-derived from a fresh read — so if
   * an account settled elsewhere between the report and the retry, the figure on
   * screen and the figure submitted were different numbers. On this console that
   * is the defect, not the edge case.
   */
  reseed: (rows: Omit<BulkRunRow, 'outcome'>[]) => void
  /** Runs the pending rows sequentially. `self` is the live signed-in account. */
  start: (self: AccountRecord) => Promise<void>
  /** Finish the row in flight, mark the rest `not-attempted`. */
  stop: () => void
  /** Re-runs only the rows that ended partial/failed/not-attempted, SAME runKey. */
  retry: (self: AccountRecord) => Promise<void>
  reset: () => void
} {
  const client = useQueryClient()
  const [state, setState] = useState<BulkRunState | null>(null)

  // The loop is async and reads its own writes, so the authoritative rows live
  // in a ref and state is a render mirror of it. Reading `state` inside the loop
  // would read the value captured when the loop started.
  const rowsRef = useRef<BulkRunRow[]>([])
  const runKeyRef = useRef('')
  const stopRef = useRef(false)
  const busyRef = useRef(false)

  /** The last published status, so `reseed` can update figures without moving it. */
  const statusRef = useRef<BulkRunState['status']>('idle')

  const publish = useCallback((status: BulkRunState['status'], cursor: number) => {
    statusRef.current = status
    const movedTotal = rowsRef.current.reduce(
      (sum, row) =>
        row.outcome.state === 'settled' || row.outcome.state === 'partial'
          ? sum + row.outcome.moved
          : sum,
      0,
    )
    setState({
      runKey: runKeyRef.current,
      rows: rowsRef.current.map((row) => ({ ...row })),
      status,
      cursor,
      movedTotal: Math.round(movedTotal * 100) / 100,
    })
  }, [])

  const prepare = useCallback(
    (rows: Omit<BulkRunRow, 'outcome'>[]) => {
      // A new plan mints a new runKey. Closing the dialog and re-selecting is a
      // new plan, and must not be able to replay the previous one's row keys.
      runKeyRef.current = crypto.randomUUID()
      stopRef.current = false
      rowsRef.current = rows.map((row) => ({ ...row, outcome: { state: 'pending' } }))
      publish('idle', -1)
    },
    [publish],
  )

  const reseed = useCallback(
    (rows: Omit<BulkRunRow, 'outcome'>[]) => {
      // Nothing to reseed before a plan exists; a stray call must not conjure one.
      if (!runKeyRef.current || rowsRef.current.length === 0) return
      const byId = new Map(rows.map((row) => [row.account.id, row]))
      rowsRef.current = rowsRef.current.map((row) => {
        // Only rows a retry may touch. A settled row's account is the historical
        // record its report line is quoted from, and overwriting it would
        // restate money that has already moved.
        if (!RETRYABLE.has(row.outcome.state)) return row
        const next = byId.get(row.account.id)
        return next ? { ...row, ...next, outcome: row.outcome } : row
      })
      // Re-publish at whatever status the run is already at. Reseed changes
      // FIGURES, never the phase — asserting 'done' here would be a lie in any
      // state but the one it happens to be called from today.
      publish(statusRef.current, -1)
    },
    [publish],
  )

  const reset = useCallback(() => {
    rowsRef.current = []
    runKeyRef.current = ''
    stopRef.current = false
    setState(null)
  }, [])

  const stop = useCallback(() => {
    stopRef.current = true
    setState((current) => (current ? { ...current, status: 'stopping' } : current))
  }, [])

  const run = useCallback(
    async (self: AccountRecord, indices: number[]) => {
      if (busyRef.current || indices.length === 0) return
      busyRef.current = true
      stopRef.current = false
      publish('running', indices[0] ?? -1)

      // The caller's balance is the only thing that moves between rows — a
      // transfer does not change their exposure — so free-to-give is threaded
      // from one running number, seeded from the live account and updated from
      // each mutation RESULT, never from what the plan predicted.
      let balance = self.balance
      const exposure = self.exposure
      let consecutiveFailures = 0

      try {
        for (let position = 0; position < indices.length; position++) {
          const index = indices[position]!
          const row = rowsRef.current[index]
          if (!row) continue

          if (stopRef.current) {
            for (const rest of indices.slice(position)) {
              const target = rowsRef.current[rest]
              if (target) target.outcome = { state: 'not-attempted' }
            }
            break
          }

          row.outcome = { state: 'moving' }
          publish('running', index)

          const running = { balance, exposure }
          const freeToGive = availableToGive(balance, exposure)
          const plan = settlementPlan(row.account, running, freeToGive)

          if (!plan) {
            row.outcome = {
              state: 'skipped',
              reason: `${row.account.username} settled before this row was reached`,
            }
            publish('running', index)
            continue
          }

          if (plan.possible <= 0) {
            row.outcome = {
              state: 'skipped',
              reason: api.settlementSkipReason(row.account, plan, running, freeToGive),
            }
            publish('running', index)
            continue
          }

          try {
            const result = await transferCredit({
              accountId: row.account.id,
              amount: plan.possible,
              direction: plan.direction,
              remark: 'Bulk settlement',
              bulk: true,
              // Same key for the same account, direction and amount, so a retry
              // at an unchanged amount REPLAYS rather than moving twice. A retry
              // at a smaller amount (because a cap shrank) gets a different key
              // and is a genuine second movement, which is correct.
              idempotencyKey: api.bulkSettleRowKey(
                runKeyRef.current,
                row.account.id,
                plan.direction,
                plan.possible,
              ),
            })

            const mine = result.from.id === self.id ? result.from : result.to
            if (mine.id === self.id) balance = mine.balance

            const remaining = Math.abs(Math.round(result.settlementAfter * 100) / 100)
            row.outcome =
              remaining === 0
                ? {
                    state: 'settled',
                    moved: result.transfer.amount,
                    ref: result.transfer.ref,
                    settlementAfter: result.settlementAfter,
                  }
                : {
                    state: 'partial',
                    moved: result.transfer.amount,
                    remaining,
                    ref: result.transfer.ref,
                    reason:
                      api.settlementCapReason(row.account, plan, running, freeToGive) ??
                      'partly settled',
                    settlementAfter: result.settlementAfter,
                  }
            consecutiveFailures = 0
          } catch (error) {
            row.outcome = {
              state: 'failed',
              reason: error instanceof Error ? error.message : 'That movement did not go through',
            }
            consecutiveFailures += 1
            if (consecutiveFailures >= CONSECUTIVE_FAILURE_LIMIT) {
              for (const rest of indices.slice(position + 1)) {
                const target = rowsRef.current[rest]
                if (target) target.outcome = { state: 'not-attempted' }
              }
              publish('running', index)
              break
            }
          }

          publish('running', index)
        }
      } finally {
        busyRef.current = false
        stopRef.current = false
        publish('done', -1)
        // Once, in a finally. Same key list as `useTransferCredit`, because the
        // rows that moved changed exactly the same things one transfer changes.
        for (const key of [
          'session',
          'account',
          'downline',
          'children',
          'overview',
          'transfers',
          'statement',
          'member-detail',
        ]) {
          client.invalidateQueries({ queryKey: [key] })
        }
      }
    },
    [client, publish],
  )

  const start = useCallback(
    (self: AccountRecord) =>
      run(
        self,
        rowsRef.current.flatMap((row, i) => (row.outcome.state === 'pending' ? [i] : [])),
      ),
    [run],
  )

  const retry = useCallback(
    (self: AccountRecord) => {
      // Belt to the idempotency key's braces: a settled row is removed from the
      // retry set entirely, so even a reused key could not re-submit one.
      const indices = rowsRef.current.flatMap((row, i) =>
        RETRYABLE.has(row.outcome.state) ? [i] : [],
      )
      for (const i of indices) {
        const row = rowsRef.current[i]
        if (row) row.outcome = { state: 'pending' }
      }
      return run(self, indices)
    },
    [run],
  )

  return { state, prepare, reseed, start, stop, retry, reset }
}
